import { AlertTriangle } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
  ALLOWED_EMAIL_DOMAIN,
  companyEmail,
  EMPLOYEE_STATUSES,
  EMPLOYEE_STATUS_LABELS,
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type EmployeeStatus,
  type Role,
} from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/cargofl-ui';
import { DepartmentPicker } from '@/components/pickers';
import { ApiError } from '@/lib/api';
import {
  useChangeEmployeeCode,
  useChangeRole,
  useCreateEmployee,
  useDepartments,
  useUpdateEmployee,
  type EmployeeSummary,
} from '@/lib/employees-api';
import { useCan, useCurrentUser } from '@/lib/session';

/**
 * Add or edit an employee.
 *
 * One dialog for both, because the fields are the same and two near-identical forms
 * drift apart. What is NOT the same is where each field is saved, and that is the
 * shape of this component:
 *
 *   - name, email, department, designation, phone, joining date, notes, status
 *       -> PATCH /employees/:id      (HR)
 *   - role
 *       -> PATCH /employees/:id/role (administrator; REVOKES their sessions)
 *   - employee code
 *       -> PATCH /employees/:id/code (administrator; breaks existing spreadsheets)
 *
 * The last two are separate endpoints because they have consequences the others do
 * not, and burying them in a general save would hide that. They are shown in their
 * own section, gated, and each says what it will do before it does it.
 */
export function EmployeeFormDialog({
  open,
  onOpenChange,
  employee,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when editing. */
  employee?: EmployeeSummary;
}) {
  const editing = employee !== undefined;
  const me = useCurrentUser();
  const canAdminister = useCan('manageSettings');

  const create = useCreateEmployee();
  const update = useUpdateEmployee(employee?.id ?? '');
  const changeRole = useChangeRole(employee?.id ?? '');
  const changeCode = useChangeEmployeeCode(employee?.id ?? '');
  const { data: departments } = useDepartments();

  const [form, setForm] = React.useState(() => blank(employee));
  React.useEffect(() => {
    if (open) setForm(blank(employee));
  }, [open, employee]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const roleChanged = editing && form.role !== employee.role;
  const codeChanged = editing && form.employeeCode.trim() !== employee.employeeCode;
  const demotingSelf = roleChanged && employee?.id === me.id;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    const details = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      department: form.department.trim() || null,
      designation: form.designation.trim() || null,
      phone: form.phone.trim() || null,
      joinedAt: form.joinedAt || null,
      /**
       * Sent only if it was actually touched.
       *
       * `notes` lives on the employee DETAIL, not on the summary this dialog is opened
       * from, so the field starts empty even when the record has notes. Sending that
       * empty value would silently wipe them on every save. The API treats an absent
       * field as "leave alone", so omitting it is the correct thing to do - and the
       * only safe one without loading the detail first.
       */
      ...(form.notes.trim() !== '' ? { notes: form.notes.trim() } : {}),
    };

    try {
      if (!editing) {
        await create.mutateAsync({
          ...details,
          employeeCode: form.employeeCode.trim(),
          status: form.status,
        });
        toast.success(`${details.name} added.`);
        onOpenChange(false);
        return;
      }

      // Details first. If a later step fails, the ordinary edit is already saved -
      // which is the part the user is most likely to have spent effort on.
      await update.mutateAsync({
        ...details,
        // EXITED is refused by the API here on purpose: it would skip the clearance
        // record, which is the document saying whether they gave the laptop back.
        ...(form.status !== employee.status && form.status !== 'EXITED'
          ? { status: form.status }
          : {}),
      });

      if (codeChanged) {
        await changeCode.mutateAsync({ employeeCode: form.employeeCode.trim() });
      }
      if (roleChanged) {
        await changeRole.mutateAsync({ role: form.role });
      }

      toast.success(
        demotingSelf
          ? 'Saved. Your own role changed, so you have been signed out everywhere.'
          : `${details.name} updated.`,
      );
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? (Object.values(error.fieldErrors ?? {})[0]?.[0] ?? error.message)
          : 'That could not be saved.';
      toast.error(message);
    }
  };

  const busy =
    create.isPending || update.isPending || changeRole.isPending || changeCode.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${employee.name}` : 'Add an employee'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Changes take effect immediately. Their asset history is not affected.'
              : 'Everyone is added as a plain Employee. Roles are granted separately.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {!editing && (
              <div className="space-y-1.5">
                <Label>Employee ID</Label>
                <Input
                  value={form.employeeCode}
                  onChange={(e) => set('employeeCode', e.target.value)}
                  placeholder="EMP-1077"
                  required
                />
                <p className="text-[11px] text-muted-foreground">
                  What bulk assignment files match on.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} required />
            </div>

            <div className={`space-y-1.5 ${editing ? '' : 'sm:col-span-2'}`}>
              <Label>Email</Label>
              {/* Gmail-style: type the name, the domain is fixed beside it, so another
                  domain can't be entered. The API's refine is still the control. */}
              <div className="flex">
                <Input
                  value={form.email.split('@')[0] ?? ''}
                  onChange={(e) => set('email', companyEmail(e.target.value))}
                  placeholder="name"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="rounded-r-none"
                  aria-label="Email name"
                />
                <span className="inline-flex shrink-0 select-none items-center rounded-r-md border border-l-0 bg-muted px-3 text-sm text-muted-foreground">
                  @{ALLOWED_EMAIL_DOMAIN}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Without one they cannot sign in to see their own assets.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Department</Label>
              <DepartmentPicker
                departments={departments}
                value={form.department}
                onChange={(v) => set('department', v)}
                allowCreate
              />
            </div>

            <div className="space-y-1.5">
              <Label>Designation</Label>
              <Input
                value={form.designation}
                onChange={(e) => set('designation', e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label>Joining date</Label>
              <Input
                type="date"
                value={form.joinedAt}
                onChange={(e) => set('joinedAt', e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => set('status', v as EmployeeStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYEE_STATUSES.map((status) => (
                    <SelectItem
                      key={status}
                      value={status}
                      // Exiting someone writes a clearance record saying what they
                      // returned. A status dropdown would skip that entirely.
                      disabled={status === 'EXITED' && employee?.status !== 'EXITED'}
                    >
                      {EMPLOYEE_STATUS_LABELS[status]}
                      {status === 'EXITED' && employee?.status !== 'EXITED'
                        ? ' — use Exit & clearance'
                        : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder={
                editing ? 'Type here to add or replace their notes' : 'Anything worth recording'
              }
            />
            {editing && (
              <p className="text-[11px] text-muted-foreground">
                Existing notes are on the employee’s own page. Leaving this blank keeps them
                as they are; typing here replaces them.
              </p>
            )}
          </div>

          {/* Administration: the two changes with consequences beyond this record. */}
          {editing && canAdminister && (
            <div className="space-y-4 rounded-md border border-dashed p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Administration
              </p>

              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => set('role', v as Role)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {ROLE_DESCRIPTIONS[form.role]}
                </p>
                {roleChanged && (
                  <Alert className="border-warning/40 bg-warning/5">
                    <AlertDescription className="flex gap-2 text-xs leading-relaxed">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
                      <span>
                        This signs {demotingSelf ? 'you' : employee.name} out everywhere
                        immediately, so a reduced role takes effect at once rather than
                        whenever the session happens to expire.
                        {demotingSelf && ' You will need to sign in again.'}
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Employee ID</Label>
                <Input
                  value={form.employeeCode}
                  onChange={(e) => set('employeeCode', e.target.value)}
                />
                {codeChanged && (
                  <Alert className="border-destructive/30">
                    <AlertDescription className="flex gap-2 text-xs leading-relaxed">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
                      <span>
                        Every spreadsheet, label and bulk-assignment file quoting{' '}
                        <span className="at-num font-medium">{employee.employeeCode}</span>{' '}
                        stops matching. Only change this to correct it to the real ID from
                        your HR system.
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add employee'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FormState {
  employeeCode: string;
  name: string;
  email: string;
  department: string;
  designation: string;
  phone: string;
  joinedAt: string;
  status: EmployeeStatus;
  role: Role;
  notes: string;
}

function blank(employee?: EmployeeSummary): FormState {
  return {
    employeeCode: employee?.employeeCode ?? '',
    name: employee?.name ?? '',
    email: employee?.email ?? '',
    department: employee?.department ?? '',
    designation: employee?.designation ?? '',
    phone: employee?.phone ?? '',
    joinedAt: employee?.joinedAt ?? '',
    status: employee?.status ?? 'ACTIVE',
    role: employee?.role ?? 'EMPLOYEE',
    notes: '',
  };
}
